import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../context/API';
import { Device, DeviceType, Merchant, Store } from '../components/DeviceAssignment/types';

export interface DeviceAssignmentState {
  devices: Device[];
  filteredDevices: Device[];
  deviceTypes: DeviceType[];
  merchants: Merchant[];
  stores: Store[];
  loading: {
    devices: boolean;
    deviceTypes: boolean;
    merchants: boolean;
    stores: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    assign: boolean;
  };
  error: string | null;
}

const initialState: DeviceAssignmentState = {
  devices: [],
  filteredDevices: [],
  deviceTypes: [],
  merchants: [],
  stores: [],
  loading: {
    devices: false,
    deviceTypes: false,
    merchants: false,
    stores: false,
    create: false,
    update: false,
    delete: false,
    assign: false,
  },
  error: null,
};

export const fetchAssignmentDevices = createAsyncThunk<
  Device[],
  void,
  { rejectValue: string }
>('deviceAssignment/fetchDevices', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('devices', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch devices'
    );
  }
});

export const fetchAssignmentDeviceTypes = createAsyncThunk<
  DeviceType[],
  void,
  { rejectValue: string }
>('deviceAssignment/fetchDeviceTypes', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('device-types', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch device types'
    );
  }
});

export const fetchAssignmentMerchants = createAsyncThunk<
  Merchant[],
  void,
  { rejectValue: string }
>('deviceAssignment/fetchMerchants', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('merchant', {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch merchants'
    );
  }
});

export const fetchAssignmentStores = createAsyncThunk<
  Store[],
  string,
  { rejectValue: string }
>('deviceAssignment/fetchStores', async (merchantId, { rejectWithValue }) => {
  try {
    const response = await API.get(`/stores/by-merchant/${merchantId}`, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch stores'
    );
  }
});

export const createAssignmentDevice = createAsyncThunk<
  Device,
  Partial<Device>,
  { rejectValue: string }
>('deviceAssignment/createDevice', async (device, { rejectWithValue }) => {
  try {
    const response = await API.post('devices', device, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.status === 200) {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to create device');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to create device'
    );
  }
});

export const updateAssignmentDevice = createAsyncThunk<
  Device,
  { id: string; data: Partial<Device> },
  { rejectValue: string }
>('deviceAssignment/updateDevice', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await API.put(`devices/${id}`, data, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.status === 200) {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to update device');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to update device'
    );
  }
});

export const deleteAssignmentDevice = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('deviceAssignment/deleteDevice', async (id, { rejectWithValue }) => {
  try {
    const response = await API.delete(`devices/${id}`, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.status === 200) {
      return id;
    }
    return rejectWithValue(response.data.message || 'Failed to delete device');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to delete device'
    );
  }
});

export const assignDeviceToStore = createAsyncThunk<
  Device,
  { deviceId: string; storeId: string },
  { rejectValue: string }
>('deviceAssignment/assignDevice', async ({ deviceId, storeId }, { rejectWithValue }) => {
  try {
    const response = await API.put(`devices/${deviceId}`, { store: storeId }, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });
    if (response.data.status === 200) {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to assign device');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to assign device'
    );
  }
});

const deviceAssignmentSlice = createSlice({
  name: 'deviceAssignment',
  initialState,
  reducers: {
    clearAssignmentError: (state) => {
      state.error = null;
    },
    setAssignmentFilteredDevices: (state, action: PayloadAction<Device[]>) => {
      state.filteredDevices = action.payload;
    },
    clearAssignmentStores: (state) => {
      state.stores = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignmentDevices.pending, (state) => {
        state.loading.devices = true;
        state.error = null;
      })
      .addCase(fetchAssignmentDevices.fulfilled, (state, action) => {
        state.loading.devices = false;
        state.devices = action.payload;
        state.filteredDevices = action.payload;
      })
      .addCase(fetchAssignmentDevices.rejected, (state, action) => {
        state.loading.devices = false;
        state.error = action.payload || 'Failed to fetch devices';
      })
      .addCase(fetchAssignmentDeviceTypes.pending, (state) => {
        state.loading.deviceTypes = true;
        state.error = null;
      })
      .addCase(fetchAssignmentDeviceTypes.fulfilled, (state, action) => {
        state.loading.deviceTypes = false;
        state.deviceTypes = action.payload;
      })
      .addCase(fetchAssignmentDeviceTypes.rejected, (state, action) => {
        state.loading.deviceTypes = false;
        state.error = action.payload || 'Failed to fetch device types';
      })
      .addCase(fetchAssignmentMerchants.pending, (state) => {
        state.loading.merchants = true;
        state.error = null;
      })
      .addCase(fetchAssignmentMerchants.fulfilled, (state, action) => {
        state.loading.merchants = false;
        state.merchants = action.payload;
      })
      .addCase(fetchAssignmentMerchants.rejected, (state, action) => {
        state.loading.merchants = false;
        state.error = action.payload || 'Failed to fetch merchants';
      })
      .addCase(fetchAssignmentStores.pending, (state) => {
        state.loading.stores = true;
        state.error = null;
      })
      .addCase(fetchAssignmentStores.fulfilled, (state, action) => {
        state.loading.stores = false;
        state.stores = action.payload;
      })
      .addCase(fetchAssignmentStores.rejected, (state, action) => {
        state.loading.stores = false;
        state.error = action.payload || 'Failed to fetch stores';
      })
      .addCase(createAssignmentDevice.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createAssignmentDevice.fulfilled, (state, action) => {
        state.loading.create = false;
        state.devices.push(action.payload);
        state.filteredDevices.push(action.payload);
      })
      .addCase(createAssignmentDevice.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload || 'Failed to create device';
      })
      .addCase(updateAssignmentDevice.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateAssignmentDevice.fulfilled, (state, action) => {
        state.loading.update = false;
        const index = state.devices.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.devices[index] = action.payload;
          const filteredIndex = state.filteredDevices.findIndex((d) => d.id === action.payload.id);
          if (filteredIndex !== -1) {
            state.filteredDevices[filteredIndex] = action.payload;
          }
        }
      })
      .addCase(updateAssignmentDevice.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload || 'Failed to update device';
      })
      .addCase(deleteAssignmentDevice.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteAssignmentDevice.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.devices = state.devices.filter((d) => d.id !== action.payload);
        state.filteredDevices = state.filteredDevices.filter((d) => d.id !== action.payload);
      })
      .addCase(deleteAssignmentDevice.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload || 'Failed to delete device';
      })
      .addCase(assignDeviceToStore.pending, (state) => {
        state.loading.assign = true;
        state.error = null;
      })
      .addCase(assignDeviceToStore.fulfilled, (state, action) => {
        state.loading.assign = false;
        const index = state.devices.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.devices[index] = action.payload;
          const filteredIndex = state.filteredDevices.findIndex((d) => d.id === action.payload.id);
          if (filteredIndex !== -1) {
            state.filteredDevices[filteredIndex] = action.payload;
          }
        }
      })
      .addCase(assignDeviceToStore.rejected, (state, action) => {
        state.loading.assign = false;
        state.error = action.payload || 'Failed to assign device';
      });
  },
});

export const {
  clearAssignmentError,
  setAssignmentFilteredDevices,
  clearAssignmentStores,
} = deviceAssignmentSlice.actions;

export default deviceAssignmentSlice.reducer;
